// src/components/clinica/CampoClinico.js
// =========================================================================
// 🧩 Un campo de la ficha clínica, dibujado según su tipo en la plantilla
// (shared/especialidades/catalogo.js).
// =========================================================================
import React from 'react';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import Odontograma from './Odontograma';

export const claseEntrada = "w-full py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:outline-none focus:border-clinica-primary focus:ring-1 focus:ring-clinica-primary transition-all resize-none disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:opacity-80";
const claseEtiqueta = "block mb-1.5 text-xs font-bold text-slate-500 uppercase tracking-wide";

const CONSTANTES = [
    { id: 'tension', etiqueta: 'Tensión art.', unidad: 'mmHg', ayuda: '120/80' },
    { id: 'ritmoCardiaco', etiqueta: 'Ritmo card.', unidad: 'lpm', ayuda: '75' },
    { id: 'temperatura', etiqueta: 'Temperatura', unidad: '°C', ayuda: '36.5' },
    { id: 'saturacionO2', etiqueta: 'Sat. O2', unidad: '%', ayuda: '98' }
];

function Etiqueta({ campo, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className={claseEtiqueta}>
            {campo.etiqueta} {campo.obligatorio && <span className="text-red-500 text-sm">*</span>}
        </label>
    );
}

export default function CampoClinico({ campo, valor, onChange, disabled }) {
    const id = `campo-${campo.id}`;

    switch (campo.tipo) {
        case 'textarea':
            return (
                <div>
                    <Etiqueta campo={campo} htmlFor={id} />
                    <textarea id={id} rows={campo.filas || 3} disabled={disabled} value={valor || ''}
                              onChange={e => onChange(e.target.value)} placeholder={campo.ayuda} className={claseEntrada} />
                </div>
            );

        case 'texto':
            return (
                <div>
                    <Etiqueta campo={campo} htmlFor={id} />
                    <input id={id} type="text" disabled={disabled} value={valor || ''}
                           onChange={e => onChange(e.target.value)} placeholder={campo.ayuda} className={claseEntrada} />
                </div>
            );

        case 'numero':
            return (
                <div>
                    <Etiqueta campo={campo} htmlFor={id} />
                    <div className="relative">
                        <input id={id} type="number" inputMode="decimal" disabled={disabled || campo.calculado}
                               min={campo.min} max={campo.max} step={campo.decimales ? 1 / 10 ** campo.decimales : 1}
                               value={valor ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                               className={`${claseEntrada} ${campo.unidad ? 'pr-16' : ''}`} />
                        {campo.unidad && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-xs font-black text-slate-400 pointer-events-none">{campo.unidad}</span>
                        )}
                    </div>
                    {campo.calculado && <p className="mt-1 text-[11px] font-semibold text-slate-400">Se calcula automáticamente.</p>}
                </div>
            );

        case 'escala': {
            const pasos = [];
            for (let n = campo.min; n <= campo.max; n++) pasos.push(n);
            return (
                <div>
                    <Etiqueta campo={campo} />
                    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={campo.etiqueta}>
                        {pasos.map(n => {
                            const activo = valor === n;
                            const intensidad = (n - campo.min) / Math.max(1, campo.max - campo.min);
                            return (
                                <button key={n} type="button" disabled={disabled} role="radio" aria-checked={activo}
                                        onClick={() => onChange(activo ? '' : n)}
                                        className={`w-9 h-9 rounded-lg text-sm font-black border transition-all disabled:cursor-not-allowed ${activo ? 'text-white border-transparent shadow-md' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                                        style={activo ? { backgroundColor: `hsl(${140 - intensidad * 140}, 70%, 45%)` } : undefined}>
                                    {n}
                                </button>
                            );
                        })}
                    </div>
                    {campo.ayuda && <p className="mt-1 text-[11px] font-semibold text-slate-400">{campo.ayuda}</p>}
                </div>
            );
        }

        case 'seleccion':
            return (
                <div>
                    <Etiqueta campo={campo} htmlFor={id} />
                    <select id={id} disabled={disabled} value={valor || ''} onChange={e => onChange(e.target.value)} className={claseEntrada}>
                        <option value="">— Sin indicar —</option>
                        {campo.opciones.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {campo.ayuda && <p className="mt-1 text-[11px] font-semibold text-slate-400">{campo.ayuda}</p>}
                </div>
            );

        case 'casillas': {
            const lista = Array.isArray(valor) ? valor : [];
            const alternar = (o) => onChange(lista.includes(o) ? lista.filter(x => x !== o) : [...lista, o]);
            return (
                <div>
                    <Etiqueta campo={campo} />
                    <div className="flex flex-wrap gap-2">
                        {campo.opciones.map(o => {
                            const activo = lista.includes(o);
                            return (
                                <button key={o} type="button" disabled={disabled} aria-pressed={activo} onClick={() => alternar(o)}
                                        className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all disabled:cursor-not-allowed ${activo ? 'bg-clinica-primary text-white border-clinica-primary' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                                    {activo && <CheckOutlined sx={{ fontSize: 14, mr: 0.5 }} aria-hidden="true" />}{o}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }

        case 'sino':
            return (
                <div>
                    <Etiqueta campo={campo} />
                    <div className="flex gap-2">
                        {[[true, 'Sí'], [false, 'No']].map(([v, texto]) => (
                            <button key={texto} type="button" disabled={disabled} aria-pressed={valor === v}
                                    onClick={() => onChange(valor === v ? '' : v)}
                                    className={`px-5 py-2 rounded-xl border text-sm font-bold transition-all disabled:cursor-not-allowed ${valor === v ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                                {texto}
                            </button>
                        ))}
                    </div>
                </div>
            );

        case 'odontograma':
            return (
                <div>
                    <Etiqueta campo={campo} />
                    <Odontograma valor={valor || {}} onChange={onChange} disabled={disabled} />
                </div>
            );

        case 'constantes': {
            const vitales = valor || {};
            return (
                <div>
                    <div className="relative flex py-2 items-center mb-2">
                        <div className="flex-grow border-t border-slate-200"></div>
                        <span className="flex-shrink mx-4 text-xs font-black tracking-widest text-slate-400 uppercase">{campo.etiqueta}</span>
                        <div className="flex-grow border-t border-slate-200"></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {CONSTANTES.map(c => (
                            <div key={c.id}>
                                <label htmlFor={`vital-${c.id}`} className={claseEtiqueta}>{c.etiqueta}</label>
                                <div className="relative">
                                    <input id={`vital-${c.id}`} type="text" disabled={disabled} value={vitales[c.id] || ''}
                                           onChange={e => onChange({ ...vitales, [c.id]: e.target.value })} placeholder={c.ayuda}
                                           className={`${claseEntrada} pl-4 pr-16 py-2.5`} />
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-xs font-black text-slate-400 pointer-events-none">{c.unidad}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        default:
            return null;
    }
}
